import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Layout, { DateInput, SearchableSelect, TablePagination, usePagination } from './Layout';
import { useNavigate } from 'react-router-dom';
import { formatDate, getApiUrl } from './config';

function SalesReport() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [sales, setSales] = useState([]);
    const [customersById, setCustomersById] = useState({});
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [vehicleNoFilter, setVehicleNoFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [viewInvoice, setViewInvoice] = useState(null);
    const navigate = useNavigate();

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        if (!digits) return "";
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Customer WhatsApp number not found");
            return;
        }
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const formatPaymentSplit = (cash, upi, bank) => {
        const c = Number(cash) || 0;
        const u = Number(upi) || 0;
        const b = Number(bank) || 0;
        const parts = [];
        if (c > 0) parts.push(`Cash ₹ ${c.toFixed(2)}`);
        if (u > 0) parts.push(`UPI ₹ ${u.toFixed(2)}`);
        if (b > 0) parts.push(`Bank ₹ ${b.toFixed(2)}`);
        return parts.length > 0 ? parts.join(" | ") : "-";
    };

    const getCustomerPhone = (row) => {
        const fromReport = row.customer_contact;
        if (fromReport !== undefined && fromReport !== null && String(fromReport).trim() !== "") return fromReport;
        return customersById[row.customer_id] || "";
    };

    const buildWhatsAppMessageFromInvoice = (row, invoice) => {
        const dateText = formatDate(invoice?.date || row.date);
        const itemsLines = (invoice?.items || []).map((it, idx) => [
            `${idx + 1}. ${it.product_name || "-"}`,
            `Qty: ${Number(it.quantity || 0)}`,
            `Rate: ${Number(it.price || 0).toFixed(2)}`,
            `Amount: ${Number(it.total || 0).toFixed(2)}`
        ].join("\n"));

        const totalAmount = Number(invoice?.total_amount ?? row.total_amount ?? 0);
        const paidAmount = Number(invoice?.paid_amount ?? row.paid_amount ?? 0);
        const cumulativeBalance = Number(row.cumulative_balance ?? row.net_balance ?? 0);
        const prevBalance = cumulativeBalance - (totalAmount - paidAmount);
        const paymentsArr = Array.isArray(invoice?.payments) ? invoice.payments : [];
        const modeTotals = paymentsArr.reduce((acc, p) => {
            const key = String(p?.payment_mode || "").toLowerCase();
            const amt = Number(p?.amount || 0);
            if (!amt) return acc;
            if (key.includes("upi")) acc.upi += amt;
            else if (key.includes("bank") || key.includes("transfer")) acc.bank += amt;
            else acc.cash += amt;
            return acc;
        }, { cash: 0, upi: 0, bank: 0 });

        return [
            `Service Invoice #${row.invoice_id}`,
            ...(invoice?.job_card_no ? [`Job Card No: ${invoice.job_card_no}`] : []),
            `Date: ${dateText}`,
            `Customer: ${invoice?.customer_name || row.customer_name || '-'}`,
            ...(invoice?.customer_contact ? [`Customer Mobile: ${invoice.customer_contact}`] : []),
            `Vehicle No: ${invoice?.vehicle_no || row.vehicle_no || '-'}`,
            ...(invoice?.service_vehicle_model ? [`Model: ${invoice.service_vehicle_model}`] : []),
            ...(invoice?.service_km ? [`Km Reading: ${invoice.service_km}`] : []),
            ...(itemsLines.length > 0 ? itemsLines : [row.items_summary || "-"]),
            `Net Amount: ₹ ${totalAmount.toFixed(2)}`,
            `Previous Amt: ₹ ${prevBalance.toFixed(2)}`,
            `Total Amount: ₹ ${(prevBalance + totalAmount).toFixed(2)}`,
            `Cash Amt: ₹ ${Number(modeTotals.cash || row.cash_amount || 0).toFixed(2)}`,
            `UPI Amt: ₹ ${Number(modeTotals.upi || row.upi_amount || 0).toFixed(2)}`,
            `Bank Amt: ₹ ${Number(modeTotals.bank || row.bank_amount || 0).toFixed(2)}`,
            `Balance Amt: ₹ ${cumulativeBalance.toFixed(2)}`
        ].join("\n");
    };

    const handleWhatsApp = async (row) => {
        const phone = getCustomerPhone(row);
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Customer WhatsApp number not found");
            return;
        }
        try {
            const res = await axios.get(`${getApiUrl()}/sales/${row.invoice_id}`);
            const message = buildWhatsAppMessageFromInvoice(row, res.data);
            openWhatsApp(phone, message);
        } catch (err) {
            console.error(err);
        }
    };

    const handleExportPdf = () => {
        window.print();
    };

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/customers`);
            const map = {};
            (res.data || []).forEach(c => {
                map[c.id] = c.contact;
            });
            setCustomersById(map);
            setCustomers(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchSales = useCallback(async () => {
        try {
            const params = {};
            if (selectedCustomerId) params.customer_id = selectedCustomerId;
            if (vehicleNoFilter.trim()) params.vehicle_no = vehicleNoFilter.trim();
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/reports/sales`, { params });
            setSales(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }, [selectedCustomerId, vehicleNoFilter, fromDate, toDate]);

    const handleExportTally = async () => {
        try {
            const params = { type: "sales" };
            if (selectedCustomerId) params.customer_id = selectedCustomerId;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/exports/tally`, {
                params,
                responseType: "blob"
            });
            const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
            const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "tally_sales.xml";
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(err);
            alert("Failed to export Tally XML");
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    const {
        currentPage,
        setCurrentPage,
        totalPages,
        pagedItems: pagedSales,
        totalItems,
        startIndex,
        endIndex
    } = usePagination(sales, 10);

    const handleDelete = async (id) => {
        if (!isAdmin) return alert("Only admin can delete records");
        if (window.confirm("Are you sure you want to delete this invoice? Stock will be reversed.")) {
            try {
                await axios.delete(`${getApiUrl()}/sales/${id}`);
                fetchSales();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleView = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/sales/${id}`);
            setViewInvoice(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Service Sales Report</h2>
                <div className="row g-3 align-items-end mb-3 no-print">
                    <div className="col-md-3">
                        <label className="form-label fw-bold">From</label>
                        <DateInput value={fromDate} onChange={setFromDate} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">To</label>
                        <DateInput value={toDate} onChange={setToDate} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Vehicle No</label>
                        <input
                            type="text"
                            className="form-control"
                            value={vehicleNoFilter}
                            onChange={e => setVehicleNoFilter(e.target.value)}
                            placeholder="Search vehicle no"
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Customer</label>
                        <SearchableSelect
                            value={selectedCustomerId}
                            options={customers.map(c => ({ value: String(c.id), label: c.name || '' }))}
                            placeholder="All customers..."
                            onChange={(nextId) => setSelectedCustomerId(nextId)}
                            allowClear
                        />
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-primary w-100" onClick={handleExportTally}>
                            Export Tally XML
                        </button>
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-outline-dark w-100" onClick={handleExportPdf}>
                            Export PDF
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Vehicle No</th>
                                <th>Model</th>
                                <th>Job Card</th>
                                <th>Items</th>
                                <th>Qty</th>
                                <th>Invoice Total</th>
                                <th>Paid Amount</th>
                                <th>Paid Split</th>
                                <th>Receipt</th>
                                <th>Receipt Split</th>
                                <th>Net Bal</th>
                                <th className="no-print">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedSales.map(s => (
                                <tr key={s.invoice_id}>
                                    <td>{formatDate(s.date)}</td>
                                    <td>{s.customer_name}</td>
                                    <td>{s.vehicle_no || '-'}</td>
                                    <td>{s.service_vehicle_model || '-'}</td>
                                    <td>{s.job_card_no || '-'}</td>
                                    <td>{s.items_summary || '-'}</td>
                                    <td>{Number(s.total_qty || 0)}</td>
                                    <td className="fw-bold">₹ {(Number(s.total_amount) || 0).toFixed(2)}</td>
                                    <td className="text-success">₹ {(Number(s.paid_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(s.cash_amount, s.upi_amount, s.bank_amount)}</td>
                                    <td className="text-success">₹ {(Number(s.receipt_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(s.receipt_cash_amount, s.receipt_upi_amount, s.receipt_bank_amount)}</td>
                                    <td className="text-danger fw-bold">₹ {(Number(s.cumulative_balance ?? s.net_balance) || 0).toFixed(2)}</td>
                                    <td className="no-print">
                                        <button className="btn btn-info btn-sm me-1" onClick={() => handleView(s.invoice_id)}>View</button>
                                        <button className="btn btn-success btn-sm me-1" onClick={() => handleWhatsApp(s)}>WhatsApp</button>
                                        {isAdmin && (
                                            <>
                                                <button type="button" className="btn btn-warning btn-sm me-1" onClick={() => navigate(`/sale?edit=${s.invoice_id}`)}>Edit</button>
                                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(s.invoice_id)}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <TablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onPageChange={setCurrentPage}
                />

                {viewInvoice && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Service Invoice #{viewInvoice.id}</h5>
                                    <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Job Card No:</strong> {viewInvoice.job_card_no || '-'}</p>
                                    <p><strong>Date:</strong> {formatDate(viewInvoice.date)}</p>
                                    <p><strong>Customer:</strong> {viewInvoice.customer_name || '-'}</p>
                                    <p><strong>Customer Mobile:</strong> {viewInvoice.customer_contact || '-'}</p>
                                    <p><strong>Vehicle No:</strong> {viewInvoice.vehicle_no || '-'}</p>
                                    <p><strong>Model:</strong> {viewInvoice.service_vehicle_model || '-'}</p>
                                    <p><strong>Km Reading:</strong> {viewInvoice.service_km || '-'}</p>
                                    <p><strong>Paid Amount:</strong> ₹ {(Number(viewInvoice.paid_amount) || 0).toFixed(2)} ({formatPaymentSplit(viewInvoice.cash_amount, viewInvoice.upi_amount, viewInvoice.bank_amount)})</p>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Item / Service</th>
                                                <th>Quantity</th>
                                                <th>Rate</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(viewInvoice.items || []).map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.product_name}</td>
                                                    <td>{Number(item.quantity || 0)}</td>
                                                    <td>{Number(item.price || 0).toFixed(2)}</td>
                                                    <td>{Number(item.total || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan="3" className="text-end">Grand Total</th>
                                                <th>₹ {(Number(viewInvoice.total_amount) || 0).toFixed(2)}</th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setViewInvoice(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default SalesReport;
